import React, { useMemo } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../lib/query-keys';
import {
  getOrgAlerts,
  type AlertCategory,
  type AlertListItem,
  type AlertSeverity,
  type AlertStatus,
} from '../lib/api';
import {
  Alert,
  CategoryIcon,
  EmptyState,
  PageHeader,
  Select,
  SeverityBadge,
  Spinner,
  WorkspaceTag,
  alertCategoryLabel,
} from '@aigovops/design-system';
import { fmtDate } from '../lib/format';

/**
 * /o/:orgSlug/alerts — Organization-level alerts inbox (V3-8-5).
 *
 * Cross-workspace alert aggregation for org admins/viewers. Read-only
 * view — triage actions (convert, snooze, dismiss) happen at the
 * workspace level. Each alert row links to its workspace-scoped detail.
 *
 * Auth: organizationContext() enforces org membership on the server.
 */

const CATEGORY_VALUES: AlertCategory[] = ['legislation', 'litigation', 'framework', 'breach', 'cert'];
const SEVERITY_VALUES: AlertSeverity[] = ['critical', 'high', 'elevated', 'moderate', 'info'];
const STATUS_VALUES: AlertStatus[] = ['open', 'converted', 'closed'];

function isCategory(v: string | null): v is AlertCategory {
  return v !== null && (CATEGORY_VALUES as string[]).includes(v);
}
function isSeverity(v: string | null): v is AlertSeverity {
  return v !== null && (SEVERITY_VALUES as string[]).includes(v);
}
function isStatus(v: string | null): v is AlertStatus {
  return v !== null && (STATUS_VALUES as string[]).includes(v);
}

export function OrgAlerts() {
  const { orgSlug } = useParams<{ orgSlug: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawStatus = searchParams.get('status');
  const rawCategory = searchParams.get('category');
  const rawSeverity = searchParams.get('severity');

  const statusFilter: AlertStatus | 'all' =
    rawStatus === 'all' ? 'all' : isStatus(rawStatus) ? rawStatus : 'open';
  const categoryFilter: AlertCategory | 'all' = isCategory(rawCategory) ? rawCategory : 'all';
  const severityFilter: AlertSeverity | 'all' = isSeverity(rawSeverity) ? rawSeverity : 'all';

  const setParam = (key: string, value: string | null) => {
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev);
        if (value === null || value === '') params.delete(key);
        else params.set(key, value);
        return params;
      },
      { replace: true },
    );
  };

  const alertsQuery = useQuery({
    queryKey: queryKeys.orgAlerts(orgSlug ?? '', { status: statusFilter, category: categoryFilter, severity: severityFilter }),
    queryFn: () => getOrgAlerts(orgSlug!, {
            status: statusFilter === 'all' ? 'all' : statusFilter,
            category: categoryFilter === 'all' ? undefined : categoryFilter,
            severity: severityFilter === 'all' ? undefined : severityFilter,
            limit: 200,
          }),
    enabled: !!orgSlug,
  });
  const alerts = {
    data: alertsQuery.data ?? null,
    error: alertsQuery.error?.message ?? null,
    loading: alertsQuery.isLoading,
  };

  const rows: AlertListItem[] = alerts.data?.alerts ?? [];
  const serverTotal = alerts.data?.total ?? 0;

  // Hero counts scoped to open alerts
  const openRows = useMemo(() => rows.filter((r) => r.status === 'open'), [rows]);
  const heroCounts = useMemo(() => {
    const critical = openRows.filter((r) => r.severity === 'critical').length;
    const high = openRows.filter((r) => r.severity === 'high').length;
    return { critical, high, total: openRows.length };
  }, [openRows]);

  // Distinct workspaces in the current result set
  const workspaceNames = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      if (r.workspace_name) set.add(r.workspace_name);
    }
    return [...set].sort();
  }, [rows]);

  if (alerts.loading && alerts.data === null) return <Spinner label="Loading alerts" />;
  if (alerts.error) return <Alert variant="error">{alerts.error}</Alert>;

  const noFiltersActive =
    statusFilter === 'open' && categoryFilter === 'all' && severityFilter === 'all';
  const showEmptyState = noFiltersActive && rows.length === 0;

  return (
    <div style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <PageHeader
        title="Alerts"
        subtitle={`${serverTotal} total across ${workspaceNames.length} workspace${workspaceNames.length === 1 ? '' : 's'}`}
      />

      {/* Hero summary tiles */}
      <div
        role="group"
        aria-label="Alert summary"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: 'var(--space-3)',
        }}
      >
        <HeroTile
          label="Open"
          value={heroCounts.total}
          sub="across all workspaces"
          onClick={() => setParam('status', null)}
        />
        <HeroTile
          label="Critical"
          value={heroCounts.critical}
          sub="need action now"
          tone="critical"
          onClick={() => setParam('severity', 'critical')}
        />
        <HeroTile
          label="High"
          value={heroCounts.high}
          sub="elevated priority"
          tone="warning"
          onClick={() => setParam('severity', 'high')}
        />
      </div>

      {/* Filters */}
      <div
        style={{
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-card)',
          background: 'var(--surface)',
          padding: 'var(--space-4)',
          display: 'flex',
          flexWrap: 'wrap',
          gap: 'var(--space-4)',
          alignItems: 'flex-end',
        }}
      >
        <FilterField id="org-alerts-status" label="Status">
          <Select
            value={statusFilter}
            onChange={(e) => setParam('status', e.target.value === 'open' ? null : e.target.value)}
          >
            <option value="open">Open</option>
            <option value="all">All</option>
            <option value="converted">Converted</option>
            <option value="closed">Closed</option>
          </Select>
        </FilterField>
        <FilterField id="org-alerts-category" label="Category">
          <Select
            value={categoryFilter}
            onChange={(e) =>
              setParam('category', e.target.value === 'all' ? null : e.target.value)
            }
          >
            <option value="all">All</option>
            {CATEGORY_VALUES.map((c) => (
              <option key={c} value={c}>
                {alertCategoryLabel(c)}
              </option>
            ))}
          </Select>
        </FilterField>
        <FilterField id="org-alerts-severity" label="Severity">
          <Select
            value={severityFilter}
            onChange={(e) =>
              setParam('severity', e.target.value === 'all' ? null : e.target.value)
            }
          >
            <option value="all">All</option>
            <option value="critical">Critical</option>
            <option value="high">High</option>
            <option value="elevated">Elevated</option>
            <option value="moderate">Moderate</option>
            <option value="info">Info</option>
          </Select>
        </FilterField>
      </div>

      {showEmptyState ? (
        <EmptyState
          title="No alerts across your workspaces"
          description="Alerts from legislation, litigation, breach intel, and framework scanners will appear here when detected in any child workspace."
        />
      ) : (
        <ul
          aria-label="Alerts across organization workspaces"
          style={{
            listStyle: 'none',
            margin: 0,
            padding: 0,
            display: 'grid',
            gap: 'var(--space-2)',
          }}
        >
          {rows.map((a) => (
            <OrgAlertRow key={a.alert_id} alert={a} />
          ))}
          {rows.length === 0 ? (
            <li
              style={{
                padding: 'var(--space-6)',
                textAlign: 'center',
                color: 'var(--text-muted)',
                border: '1px dashed var(--border-strong)',
                borderRadius: 'var(--radius-card)',
              }}
            >
              No alerts match the current filters.
            </li>
          ) : null}
        </ul>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

function FilterField({
  id,
  label,
  children,
}: {
  id: string;
  label: string;
  children: React.ReactElement;
}) {
  return (
    <div style={{ minWidth: '160px' }}>
      <label
        htmlFor={id}
        style={{
          display: 'block',
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-muted)',
          marginBottom: '4px',
        }}
      >
        {label}
      </label>
      {React.cloneElement(children, { id })}
    </div>
  );
}

function HeroTile({
  label,
  value,
  sub,
  tone,
  onClick,
}: {
  label: string;
  value: number;
  sub: string;
  tone?: 'critical' | 'warning';
  onClick?: () => void;
}) {
  const color =
    tone === 'critical'
      ? 'var(--critical)'
      : tone === 'warning'
        ? 'var(--warning)'
        : 'var(--text)';
  const bg =
    tone === 'critical'
      ? 'var(--danger-bg)'
      : tone === 'warning'
        ? 'var(--warning-bg)'
        : 'var(--surface)';
  const borderColor =
    tone === 'critical'
      ? 'var(--critical)'
      : tone === 'warning'
        ? 'var(--warning)'
        : 'var(--border)';
  const content = (
    <>
      <div
        style={{
          fontSize: 'var(--font-size-3xs)',
          color: 'var(--text-muted)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-2xl)',
          color,
          fontWeight: 700,
          fontFamily: 'var(--font-mono)',
        }}
      >
        {value}
      </div>
      <div style={{ fontSize: 'var(--font-size-3xs)', color: 'var(--text-muted)' }}>{sub}</div>
    </>
  );
  const style: React.CSSProperties = {
    border: `1px solid ${borderColor}`,
    borderRadius: 'var(--radius-card)',
    background: bg,
    padding: 'var(--space-3) var(--space-4)',
    textAlign: 'left' as const,
    cursor: onClick ? 'pointer' : 'default',
  };
  if (onClick) {
    return (
      <button
        type="button"
        onClick={onClick}
        style={{ ...style, minHeight: 'var(--min-touch-target)' }}
      >
        {content}
      </button>
    );
  }
  return <div style={style}>{content}</div>;
}

function OrgAlertRow({ alert }: { alert: AlertListItem }) {
  const domId = `org-alert-${alert.alert_id}`;
  // Link to workspace-scoped detail
  const href = alert.workspace_slug
    ? `/w/${alert.workspace_slug}/app/alerts/${alert.alert_id}`
    : '#';

  return (
    <li
      aria-labelledby={`${domId}-t`}
      aria-describedby={`${domId}-meta`}
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 'var(--space-3)',
        padding: 'var(--space-3)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-card)',
        background: 'var(--surface)',
      }}
    >
      <CategoryIcon category={alert.category} />
      <div style={{ flex: 1, minWidth: 0, display: 'grid', gap: '4px' }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 'var(--space-2)',
            flexWrap: 'wrap',
          }}
        >
          <WorkspaceTag
            name={alert.workspace_name ?? 'Unknown workspace'}
            slug={alert.workspace_slug}
          />
          <SeverityBadge severity={alert.severity === 'info' ? 'standard' : alert.severity} />
          <Link
            to={href}
            id={`${domId}-t`}
            style={{
              color: 'var(--text)',
              fontWeight: 500,
              textDecoration: 'none',
            }}
          >
            {alert.title}
          </Link>
        </div>
        <span
          id={`${domId}-meta`}
          style={{
            fontSize: 'var(--font-size-3xs)',
            color: 'var(--text-muted)',
          }}
        >
          {alertCategoryLabel(alert.category)}
          {alert.deadline_at ? ` · Deadline ${fmtDate(alert.deadline_at)}` : ''}
          {' · '}
          {fmtDate(alert.created_at)}
          {' · '}
          {alert.status}
        </span>
      </div>
    </li>
  );
}
